class SDKLoader {
  constructor() {
    this.loadingPromises = new Map();
    this.loadedSDKs = new Set();
    this.plivoSdkInstances = new Map();
    this.plivoCredentials = {};
    this.currentPlivoProvider = null;
  }

  async loadPlivoSDK() {
    if (this.loadedSDKs.has("plivo")) {
      return window.Plivo;
    }

    if (this.loadingPromises.has("plivo")) {
      return this.loadingPromises.get("plivo");
    }

    const loadPromise = this._loadPlivoSDK();
    this.loadingPromises.set("plivo", loadPromise);

    try {
      const result = await loadPromise;
      this.loadedSDKs.add("plivo");
      return result;
    } catch (error) {
      this.loadingPromises.delete("plivo");
      throw error;
    }
  }

  async _loadPlivoSDK() {
    try {
      if (window.Plivo) {
        console.log("Plivo SDK already available on window");
        return window.Plivo;
      }

      console.log("Loading Plivo SDK...");
      const PlivoSDK = await import("plivo-browser-sdk");
      window.Plivo = PlivoSDK.default || PlivoSDK;
      console.log("Plivo SDK loaded and attached to window");
      return window.Plivo;
    } catch (error) {
      console.error("Failed to load Plivo SDK:", error);
      throw new Error(`Failed to load Plivo SDK: ${error.message}`);
    }
  }

  async initializePlivoSDK(provider) {
    if (this.plivoSdkInstances.has(provider)) {
      console.log(`[sdkLoader] Returning existing SDK instance for ${provider}`);
      return this.plivoSdkInstances.get(provider);
    }

    console.log(`[sdkLoader] Creating NEW SDK instance for ${provider}`);
    const Plivo = await this.loadPlivoSDK();
    
    const options = {
      permOnClick: true,
      enableTracking: true,
      closeProtection: false,
      maxAverageBitrate: 48000,
      allowMultipleIncomingCalls: false,
    };

    const sdkInstance = new Plivo(options);
    
    if (sdkInstance.client) {
      sdkInstance.client.setDebug("ERROR");
    }
    
    this.plivoSdkInstances.set(provider, sdkInstance);
    console.log(`[sdkLoader] SDK instance created and stored for ${provider}. Total instances: ${this.plivoSdkInstances.size}`);
    return sdkInstance;
  }

  storePlivoCredentials(provider, credentials) {
    this.plivoCredentials[provider] = credentials;
  }

  async switchPlivoProvider(provider) {
    if (!this.plivoCredentials[provider]) {
      throw new Error(`No credentials found for provider: ${provider}`);
    }

    const sdkInstance = this.plivoSdkInstances.get(provider);
    
    if (!sdkInstance) {
      throw new Error(`SDK instance not initialized for provider: ${provider}`);
    }
    
    if (this.currentPlivoProvider === provider && sdkInstance.client?.isLoggedIn) {
      if (sdkInstance.client?.userName === this.plivoCredentials[provider]?.username) {
        return true;
      }
    }

    if (sdkInstance.client.isLoggedIn) {
      await this.logoutPlivo(provider);
    }

    const { username, password } = this.plivoCredentials[provider];
    
    await this.loginPlivo(provider, username, password);
    this.currentPlivoProvider = provider;
    console.log(`[sdkLoader] Successfully switched to provider: ${provider}`);
    return true;
  }

  async loginPlivo(provider, username, password) {
    const sdkInstance = this.plivoSdkInstances.get(provider);
    
    return new Promise((resolve, reject) => {
      const loginTimeout = setTimeout(() => {
        sdkInstance.client.off("onLogin", onLoginSuccess);
        sdkInstance.client.off("onLoginFailed", onLoginFailed);
        reject(new Error("Login timeout"));
      }, 10000);

      const onLoginSuccess = () => {
        clearTimeout(loginTimeout);
        sdkInstance.client.off("onLogin", onLoginSuccess);
        sdkInstance.client.off("onLoginFailed", onLoginFailed);
        resolve();
      };

      const onLoginFailed = (cause) => {
        clearTimeout(loginTimeout);
        sdkInstance.client.off("onLogin", onLoginSuccess);
        sdkInstance.client.off("onLoginFailed", onLoginFailed);
        reject(new Error(`Login failed: ${cause}`));
      };

      sdkInstance.client.on("onLogin", onLoginSuccess);
      sdkInstance.client.on("onLoginFailed", onLoginFailed);
      sdkInstance.client.login(username, password);
    });
  }

  async logoutPlivo(provider) {
    const sdkInstance = this.plivoSdkInstances.get(provider);
    
    return new Promise((resolve) => {
      if (!sdkInstance || !sdkInstance.client.isLoggedIn) {
        resolve();
        return;
      }

      const onLogout = () => {
        sdkInstance.client.off("onLogout", onLogout);
        resolve();
      };

      sdkInstance.client.on("onLogout", onLogout);
      sdkInstance.client.logout();
    });
  }

  getPlivoSDKInstance(provider) {
    return this.plivoSdkInstances.get(provider);
  }

  isPlivoLoggedIn(provider) {
    const sdkInstance = this.plivoSdkInstances.get(provider);
    const isLoggedIn = sdkInstance?.client?.isLoggedIn || false;
    return isLoggedIn;
  }
  
  getCurrentPlivoProvider() {
    return this.currentPlivoProvider;
  }

  async loadTwilioSDK() {
    if (this.loadedSDKs.has("twilio")) {
      return true;
    }

    if (this.loadingPromises.has("twilio")) {
      return this.loadingPromises.get("twilio");
    }

    const loadPromise = this._loadTwilioSDK();
    this.loadingPromises.set("twilio", loadPromise);

    try {
      const result = await loadPromise;
      this.loadedSDKs.add("twilio");
      return result;
    } catch (error) {
      this.loadingPromises.delete("twilio");
      throw error;
    }
  }

  async _loadTwilioSDK() {
    try {
      console.log("Twilio SDK already loaded via npm import");
      return true;
    } catch (error) {
      console.error("Failed to load Twilio SDK:", error);
      throw new Error(`Failed to load Twilio SDK: ${error.message}`);
    }
  }

  async loadAllSDKs() {
    const results = {};

    try {
      results.twilio = await this.loadTwilioSDK();
    } catch (error) {
      console.error("Twilio SDK loading failed:", error);
      results.twilio = null;
    }

    try {
      results.plivo = await this.loadPlivoSDK();
    } catch (error) {
      console.error("Plivo SDK loading failed:", error);
      results.plivo = null;
    }

    return results;
  }

  isSDKLoaded(sdk) {
    return this.loadedSDKs.has(sdk);
  }

  getLoadedSDKs() {
    return Array.from(this.loadedSDKs);
  }

  async waitForSDK(sdk, timeout = 10000) {
    if (this.isSDKLoaded(sdk)) {
      return true;
    }

    return new Promise((resolve, reject) => {
      const checkInterval = 100;
      let elapsed = 0;

      const interval = setInterval(() => {
        if (this.isSDKLoaded(sdk)) {
          clearInterval(interval);
          resolve(true);
        } else {
          elapsed += checkInterval;
          if (elapsed >= timeout) {
            clearInterval(interval);
            reject(new Error(`Timeout waiting for ${sdk} SDK to load`));
          }
        }
      }, checkInterval);
    });
  }
}

export default new SDKLoader();
